// combatpro/treasure/treasureCatalogue.js
//
// Runtime loader + indexer for the external Treasure catalogue. The catalogue is
// data under public/data/treasure (manifest + sharded item/profile/preset files +
// currency), generated separately by Codex. Nothing here hand-authors content.
//
// buildCatalogue() is a PURE merge/index/dedupe/validate step (used by tests with
// fixtures). fetchCatalogue() performs the network load then calls buildCatalogue.
// De-duplication by id makes double file-loading harmless. No DOM, no optional
// chaining, no em dash.

import { validateManifest, validateItems, validateProfiles, validateCurrency } from './treasureValidate';

var CONTRACT_MAJOR = 1;

function majorOf(semver) {
  if (typeof semver !== 'string') { return NaN; }
  return parseInt(semver.split('.')[0], 10);
}

// Merge shard arrays, dropping records whose id was already seen (first wins).
function mergeById(shards) {
  var out = [];
  var seen = {};
  var dupes = 0;
  for (var s = 0; s < shards.length; s++) {
    var arr = Array.isArray(shards[s]) ? shards[s] : [];
    for (var i = 0; i < arr.length; i++) {
      var rec = arr[i];
      if (!rec || typeof rec.id !== 'string') { out.push(rec); continue; }
      if (seen[rec.id]) { dupes++; continue; }
      seen[rec.id] = true;
      out.push(rec);
    }
  }
  return { list: out, dupes: dupes };
}

export function buildCatalogue(manifest, currency, itemShards, profileShards, presetShards) {
  var errors = [];
  var mv = validateManifest(manifest);
  if (!mv.valid) { errors = errors.concat(mv.errors); }
  var vocab = (manifest && manifest.vocab) ? manifest.vocab : { categories: [], rarities: [], wealthTiers: [] };

  if (manifest && majorOf(manifest.contractVersion) !== CONTRACT_MAJOR) {
    errors.push('manifest.contractVersion major mismatch: engine supports ' + CONTRACT_MAJOR + '.x, got ' + manifest.contractVersion);
  }

  var mergedItems = mergeById(itemShards || []);
  var mergedProfiles = mergeById(profileShards || []);
  var mergedPresets = mergeById(presetShards || []);

  var iv = validateItems(mergedItems.list, vocab, {});
  if (!iv.valid) { errors = errors.concat(iv.errors); }
  var pv = validateProfiles(mergedProfiles.list, vocab);
  if (!pv.valid) { errors = errors.concat(pv.errors); }
  var cv = validateCurrency(currency);
  if (!cv.valid) { errors = errors.concat(cv.errors); }

  // Index.
  var byCategory = {};
  var itemsById = {};
  mergedItems.list.forEach(function (it) {
    if (!it || typeof it.id !== 'string') { return; }
    itemsById[it.id] = it;
    if (!byCategory[it.category]) { byCategory[it.category] = []; }
    byCategory[it.category].push(it);
  });

  var profilesByKind = {};
  mergedProfiles.list.forEach(function (p) {
    if (!p || typeof p.kind !== 'string') { return; }
    if (!profilesByKind[p.kind]) { profilesByKind[p.kind] = []; }
    profilesByKind[p.kind].push(p);
  });

  var presetsById = {};
  mergedPresets.list.forEach(function (p) { if (p && p.id) { presetsById[p.id] = p; } });

  return {
    valid: errors.length === 0,
    errors: errors,
    dupes: mergedItems.dupes + mergedProfiles.dupes + mergedPresets.dupes,
    vocab: vocab,
    currency: currency,
    items: mergedItems.list,
    itemsById: itemsById,
    byCategory: byCategory,
    profiles: mergedProfiles.list,
    profilesByKind: profilesByKind,
    presets: mergedPresets.list,
    presetsById: presetsById,
    manifest: manifest
  };
}

function joinPath(base, rel) {
  if (/^https?:/i.test(rel)) { return rel; }
  var b = base.replace(/\/+$/, '');
  return b + '/' + String(rel).replace(/^\/+/, '');
}

// fetchCatalogue(baseUrl) -> Promise<catalogue>. baseUrl defaults to the same
// /data convention the spell/monster DBs use. Fails soft: on any network/parse
// error it returns an empty-but-valid-shaped catalogue with errors populated, so
// the UI degrades to "no catalogue loaded" instead of crashing.
export function fetchCatalogue(baseUrl) {
  var base = baseUrl || '/data/treasure';
  function getJson(url) {
    return fetch(url, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) { throw new Error('HTTP ' + r.status + ' for ' + url); }
      return r.json();
    });
  }
  return getJson(joinPath(base, 'manifest.json')).then(function (manifest) {
    var currencyP = getJson(joinPath(base, manifest.currency || 'currency.json'));
    var itemsP = Promise.all((manifest.shards.items || []).map(function (p) { return getJson(joinPath(base, p)); }));
    var profP = Promise.all(((manifest.shards.lootProfiles) || []).map(function (p) { return getJson(joinPath(base, p)); }));
    var presP = Promise.all(((manifest.shards.presets) || []).map(function (p) { return getJson(joinPath(base, p)); }));
    return Promise.all([currencyP, itemsP, profP, presP]).then(function (parts) {
      return buildCatalogue(manifest, parts[0], parts[1], parts[2], parts[3]);
    });
  }).catch(function (err) {
    return { valid: false, errors: ['catalogue load failed: ' + (err && err.message ? err.message : String(err))],
      vocab: { categories: [], rarities: [], wealthTiers: [] }, currency: null, items: [], itemsById: {},
      byCategory: {}, profiles: [], profilesByKind: {}, presets: [], presetsById: {}, manifest: null, dupes: 0 };
  });
}
